import React from 'react';
import dayjs from 'dayjs';
import {Radio, Typography} from 'antd';
import {RadioChangeEvent} from 'antd/es/radio';

import {useLocation, useNavigate} from 'react-router-dom';
import {BookingListState} from '../../../types/bookingTypes';
import BookingList from '../../../components/booking/BookingList';
import ErrorBoundary from '../../../components/ErrorBoundary';
import {formatTimestamp} from '../../../helpers/misc';
import {messageType, showMessages} from '../../../helpers/message';
import {useAppSelector} from '../../../hooks/useStore';
import {selectAccountMembershipNumber, selectActiveMembershipId, selectIsStaff} from '../../../store/slices/userSlice';
import {useLazyGetBookingsQuery} from '../../../store/api/bookingsApi';

const MyBookings = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const accountMemberNum = useAppSelector(selectAccountMembershipNumber);
    const membershipID = useAppSelector(selectActiveMembershipId);
    const isStaff = useAppSelector(selectIsStaff);

    const [bookingList, setBookingList] = React.useState<BookingListState>({
        upcoming: [],
        current: [],
        past: [],
        cancelled: []
    });
    const [total, setTotal] = React.useState<number>(0);
    const [perPage, setPerPage] = React.useState<number>(10);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [currentPage, setCurrentPage] = React.useState<number>(1);
    const mode = React.useRef<'upcoming' | 'current' | 'past' | 'cancelled'>(location.state?.tab ? location.state.tab : 'current');

    const [getBookings, {isLoading: bookingsLoading, error: bookingsError}] = useLazyGetBookingsQuery();

    const buildQueryString = React.useCallback((mode: string, page: number, pageSize: number = 10): string => {
        const now = formatTimestamp(dayjs());
        const baseQuery = {page, pageSize, membershipID: membershipID, descSort: 0};
        switch (mode) {
            case 'upcoming':
                return JSON.stringify({...baseQuery, searchType: 'contained_by', startedAt: now, state: 'CONFIRMED'});
            case 'current':
                return JSON.stringify({...baseQuery, searchType: 'contains', startedAt: now, endedAt: now, state: 'CONFIRMED'});
            case 'past':
                return JSON.stringify({...baseQuery, searchType: 'contained_by', endedAt: now, descSort: 1, state: 'CONFIRMED'});
            case 'cancelled':
                return JSON.stringify({...baseQuery, state: 'CANCELLED'});
            default:
                return '';
        }
    }, [membershipID]);

    const fetchBookings = React.useCallback(async (queryString: string, currentMode: string) => {
        setLoading(true);
        try {
            const query = JSON.parse(queryString);
            const response = await getBookings(query).unwrap();

            setBookingList((prev) => ({...prev, [currentMode]: response.data}));
            setTotal(response.meta.total);
            setCurrentPage(response.meta.current_page);
            setPerPage(response.meta.per_page);
        } catch (error: any) {
            showMessages(error.message, messageType.error);
            if (error.status_code === 404) navigate('/404');
        } finally {
            setLoading(false);
        }
    }, [getBookings, navigate]);

    React.useEffect(() => {
        const loadBookings = async () => {
            if (!bookingsLoading && !bookingsError) {
                // Reset location state upon refresh/initial load.
                window.history.replaceState(null, '');
                const queryString = buildQueryString(mode.current, 1);
                await fetchBookings(queryString, mode.current);
            }
        };

        if (isStaff && !accountMemberNum) {
            navigate('/member_change');
        } else {
            loadBookings();
        }
    }, [bookingsLoading, bookingsError, isStaff, accountMemberNum, navigate, fetchBookings, buildQueryString]);

    const handleRadioChange = async (event: RadioChangeEvent) => {
        event.preventDefault();
        const newMode = event.target.value;
        mode.current = newMode;
        const queryString = buildQueryString(newMode, 1);
        await fetchBookings(queryString, newMode);
    };

    const handlePageChange = async (page: number, pageSize: number) => {
        const queryString = buildQueryString(mode.current, page, pageSize);
        await fetchBookings(queryString, mode.current);
    };

    const refreshBookings = async () => {
        const queryString = buildQueryString(mode.current, currentPage, perPage);
        await fetchBookings(queryString, mode.current);
    };

    return (
        <ErrorBoundary>
            <Typography.Title level={3}>My Bookings</Typography.Title>
            <div style={{marginBottom: 16}}>
                <Radio.Group value={mode.current} buttonStyle="solid" onChange={handleRadioChange}>
                    <Radio.Button value="upcoming">Upcoming</Radio.Button>
                    <Radio.Button value="current">Current</Radio.Button>
                    <Radio.Button value="past">Past</Radio.Button>
                    <Radio.Button value="cancelled">Cancelled</Radio.Button>
                </Radio.Group>
            </div>
            <BookingList
                loading={bookingsLoading || loading}
                bookings={bookingList[mode.current]}
                paginationParam={{
                    total: total || bookingList[mode.current].length,
                    pageSize: perPage,
                    current: currentPage,
                    onChange: handlePageChange,
                    showSizeChanger: true,
                    onShowSizeChange: handlePageChange,
                }}
                onBookingUpdate={refreshBookings}
            />
        </ErrorBoundary>
    );
};

export default MyBookings;
